import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGetCurrentUserProfileQuery } from '../../redux/api/authorized/auth';
import { selectPrivyToken } from '../../redux/slices/auth';
import { useSelector } from 'react-redux';
import {
  useCreateGiftMutation,
  useUpdateGiftMutation,
  usePrepareTransactionsMutation,
  useBroadcastAndTrackMutation,
  useGetGiftQuery,
  useGetGiftsQuery,
  useCreateTrialGiftMutation,
} from '../../redux/api/authorized/gifts';
import {
  selectTokenByNetworkAndAddress,
  selectTokenByNetworkAndSymbol,
  selectLastUpdated,
} from '../../redux/slices/tokenSlice';
import { useLazySearchTokensQuery } from '../../redux/api/public/tokens';
import { toTokenAmount, fromTokenAmount } from '../../hooks/useSolana';
import { useSolana } from '../../hooks/useSolana';
import { useGiftAmountCalculations } from '../../hooks/useGiftAmountCalculations';
import {
  EVENT_GIFT_FLOW_CREATE_GIFT_BUTTON_CLICKED,
  EVENT_GIFT_FLOW_GIFT_CREATED,
  EVENT_GIFT_FLOW_GIFT_FAILED,
  EVENT_FREE_GIFT_FLOW_SHOWN,
  sendEvent,
} from '../../common/analytics';
import { hapticsImpactMedium } from '../../common/haptic';
import { isFutureDate } from '../../common/date';
import Customize from '../GiftFlow/components/Customize';
import AmountSelector from '../GiftFlow/components/AmountSelector';
import ShippingAddress from '../GiftFlow/components/ShippingAddress';
import SenderInfo from '../GiftFlow/components/SenderInfo';
import Confirmation from '../GiftFlow/components/Confirmation';
import TokenSelector from '../GiftFlow/components/TokenSelector';
import { useFlag } from '@unleash/proxy-client-react';
import { getVerisoulSession } from '../../common/verisoul';
import ConfirmationSkeleton from '../GiftFlow/components/Confirmation/skeleton.jsx';
import Delivery from '../GiftFlow/components/Delivery/index.jsx';
import Preview from '../GiftFlow/components/Preview';

const giftFormSchema = yup
  .object({
    delivery: yup
      .string()
      .oneOf(['email', 'link'], 'Delivery method must be email or link')
      .required('Delivery method is required'),
    isUsingUSDC: yup.boolean(),
    userEmail: yup.string(),
    email: yup.string().when('delivery', {
      is: 'email',
      then: (schema) =>
        schema
          .email('Must be a valid email')
          .required('Email is required')
          .test(
            'email-match',
            'You cannot use your own email',
            (value, context) => value !== context.parent.userEmail,
          ),
      otherwise: (schema) => schema.optional(),
    }),
    message: yup
      .string()
      .optional()
      .max(190, 'Message must be at most 190 characters'),
    senderName: yup
      .string()
      .optional()
      .max(32, 'From field must be at most 40 characters'),
    image: yup.string().required('Card image is required'),
    network: yup.string().required('Network is required'),
    targetTokenAddress: yup.string().required('Token address is required'),
    recipientName: yup.string(),
    addressLine1: yup.string(),
    addressLine2: yup.string(),
    city: yup.string(),
    formattedAddress: yup.string(),
    state: yup.string(),
    postalCode: yup.string(),
    country: yup.string(),
    usdAmount: yup.string().when('isUsingUSDC', {
      is: true,
      then: (schema) =>
        schema
          .required('Amount is required')
          .test('is-number', 'Must be a valid number', (value) =>
            value === '' || value == null ? false : !isNaN(Number(value)),
          )
          .test('min-1', 'Minimum amount is 1', (value) => {
            const n = Number(value);
            return !isNaN(n) && n >= 1;
          }),
      otherwise: (schema) => schema.optional(),
    }),
    targetTokenAmount: yup.string().when('isUsingUSDC', {
      is: false,
      then: (schema) =>
        schema
          .required('Amount is required')
          .test('is-number', 'Must be a valid number', (value) =>
            value === '' || value == null ? false : !isNaN(Number(value)),
          )
          .test('min-1', 'Must be greater than 0', (value) => {
            const n = Number(value);
            return !isNaN(n) && n > 0;
          }),
      otherwise: (schema) => schema.optional(),
    }),
    shippingAddressId: yup.number().when('delivery', {
      is: 'mail',
      then: (schema) => schema.required('Shipping address is required'),
      otherwise: (schema) => schema.optional(),
    }),
    amountSpecifiedInUsd: yup.boolean().default(true),
  })
  .required();

const GiftFormContext = createContext(null);

const defaultInitialValues = {
  isUsingUSDC: true,
  delivery: 'link',
  usdAmount: '1',
  targetTokenAmount: '',
  amountSpecifiedInUsd: true,
};

// Step names - using strings for URL readability
export const STEP_NAMES = {
  TOKEN: 'token',
  CUSTOMIZE: 'customize',
  AMOUNT: 'amount',
  SENDER_INFO: 'sender-info',
  SHIPPING: 'shipping',
  CONFIRMATION: 'confirmation',
  DELIVERY: 'delivery',
  PREVIEW: 'preview',
};

// Define components at module level - STABLE REFERENCES
const STEP_COMPONENTS = {
  TokenSelector,
  Customize,
  AmountSelector,
  SenderInfo,
  ShippingAddress,
  Confirmation,
  Delivery,
  Preview,
};

const buildSteps = (
  delivery,
  initialIsAuthenticated,
  showTrialExperience,
  freeGiftAllInputs,
  usdAmount,
) => {
  const steps = [
    {
      name: STEP_NAMES.TOKEN,
      component: STEP_COMPONENTS.TokenSelector,
      title: 'Select Asset to Gift',
      buttonLabel: 'Continue',
    },
  ];

  if (!showTrialExperience || (showTrialExperience && freeGiftAllInputs)) {
    steps.push({
      name: STEP_NAMES.AMOUNT,
      component: STEP_COMPONENTS.AmountSelector,
      title: 'Select Amount',
      buttonLabel: 'Continue',
    });
  }

  steps.push({
    name: STEP_NAMES.CUSTOMIZE,
    component: STEP_COMPONENTS.Customize,
    title: 'Customize Gift',
    buttonLabel: 'Continue',
  });

  steps.push({
    name: STEP_NAMES.SENDER_INFO,
    component: STEP_COMPONENTS.SenderInfo,
    title: 'Sender Info',
    buttonLabel: 'Continue',
  });

  const shouldSkipDeliverySteps =
    showTrialExperience && usdAmount === '1' && delivery === 'link';

  if (!shouldSkipDeliverySteps) {
    steps.push({
      name: STEP_NAMES.DELIVERY,
      component: STEP_COMPONENTS.Delivery,
      title: 'Select Delivery Method',
      buttonLabel: 'Continue',
    });
  }

  if (delivery !== 'mail') {
    steps.push({
      name: STEP_NAMES.PREVIEW,
      component: STEP_COMPONENTS.Preview,
      title: 'Preview Gift',
      buttonLabel: 'Continue',
    });
  }

  if (delivery === 'mail') {
    steps.push({
      name: STEP_NAMES.SHIPPING,
      component: STEP_COMPONENTS.ShippingAddress,
      title: 'Shipping Details',
      buttonLabel: 'Checkout',
    });
  }

  steps.push({
    name: STEP_NAMES.CONFIRMATION,
    component: STEP_COMPONENTS.Confirmation,
    title: 'Checkout',
    buttonLabel: 'Continue',
  });

  return steps;
};

// Helper to find step index by name
const findStepIndexByName = (steps, stepName) => {
  const index = steps.findIndex((step) => step.name === stepName);
  return index >= 0 ? index : 0; // Default to first step if not found
};

// Helper to get step name by index
const getStepNameByIndex = (steps, index) => {
  return steps[index]?.name || STEP_NAMES.TOKEN;
};

export const GiftFormProvider = ({
  children,
  schema = giftFormSchema,
  mode = 'all',
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isTrialFlow = searchParams.get('trial') === '1';

  const lpd = searchParams.get('lpd');
  const showPrestockBonus =
    useFlag('showPrestockBonus') && searchParams.get('trial') !== '1';

  const showBonus = showPrestockBonus;

  const form = useForm({
    resolver: yupResolver(schema),
    mode,
    defaultValues: {
      ...defaultInitialValues,
      usdAmount: isTrialFlow ? '1' : '5',
    },
  });

  const privyToken = useSelector(selectPrivyToken);
  const isAuthenticated = !!privyToken;
  const { data: currentUser } = useGetCurrentUserProfileQuery(undefined, {
    skip: !privyToken,
  });

  const userEmail = currentUser?.email;

  const {
    watch,
    getValues,
    setValue,
    clearErrors,
    trigger,
    formState: { errors, isValid, isSubmitting },
    handleSubmit,
    control,
  } = form;

  const [selectedPaymentToken, setSelectedPaymentToken] = useState(null);
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] =
    useState(null); // 'token', 'applePay'
  const [sendingStatus, setSendingStatus] = useState('idle');
  const [feedback, setFeedback] = useState(null);
  const [claimUrl, setClaimUrl] = useState(null);
  const [gift, setGift] = useState(null);
  const [giftId, setGiftId] = useState(null);
  const [payingWith, setPayingWith] = useState(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  // API hooks for gift creation
  const [createGiftMutation] = useCreateGiftMutation();
  const [createTrialGiftMutation] = useCreateTrialGiftMutation();
  const [updateGiftMutation] = useUpdateGiftMutation();
  const [prepareTransactions] = usePrepareTransactionsMutation();
  const [broadcastAndTrack] = useBroadcastAndTrackMutation();

  // Solana utilities for transaction signing
  const {
    signAndSerializeTransaction,
    parseSolanaError,
    refreshTokenBalances,
  } = useSolana();

  // Watch form values first (needed for selectors and hooks below)
  const isUsingUSDC = watch('isUsingUSDC');
  const delivery = watch('delivery');
  const email = watch('email');
  const message = watch('message');
  const senderName = watch('senderName');
  const recipientName = watch('recipientName');
  const image = watch('image');
  const targetTokenAddress = watch('targetTokenAddress');
  const network = watch('network');
  const shippingAddressId = watch('shippingAddressId');
  const usdAmount = watch('usdAmount');
  const targetTokenAmount = watch('targetTokenAmount');
  const addressLine1 = watch('addressLine1');
  const addressLine2 = watch('addressLine2');
  const city = watch('city');
  const state = watch('state');
  const postalCode = watch('postalCode');
  const country = watch('country');
  const formattedAddress = watch('formattedAddress');
  const sendOnDate = watch('sendOnDate');
  const amountSpecifiedInUsd = watch('amountSpecifiedInUsd');

  // Get tokens from Redux (now network and targetTokenAddress are defined)
  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, network || 'solana', 'USDC'),
  );

  const targetToken = useSelector((state) =>
    targetTokenAddress && network
      ? selectTokenByNetworkAndAddress(state, network, targetTokenAddress)
      : null,
  );

  // Get triggerCreateOrder from useGiftAmountCalculations hook
  // We need this for calculating baseTokenAmount when paying with non-USDC tokens
  const { triggerCreateOrder } = useGiftAmountCalculations({
    network,
    targetTokenAddress,
    targetTokenAmount,
    usdAmount,
    amountSpecifiedInUsd,
    delivery,
    selectedPaymentMethodType,
  });

  useEffect(() => {
    if (userEmail && setValue) {
      setValue('userEmail', userEmail);
    }
  }, [userEmail, setValue]);

  useEffect(() => {
    // Clear errors and values when delivery method changes away from requiring those fields
    // Don't trigger validation immediately - let it happen on user interaction
    if (delivery !== 'email') {
      clearErrors('email');
      // Clear email value when delivery changes to 'link' (backend determines link vs email based on email param)
      if (delivery === 'link') {
        setValue('email', '', { shouldValidate: false, shouldDirty: false });
      }
    }
    if (delivery !== 'mail') {
      clearErrors('shippingAddressId');
    }
  }, [delivery, clearErrors, setValue]);


  // Automatically set delivery to 'email' when sendOnDate is a future date
  // This ensures scheduled gifts are sent via email (not link or mail)
  useEffect(() => {
    if (sendOnDate && isFutureDate(sendOnDate) && delivery !== 'email' && delivery !== 'mail') {
      setValue('delivery', 'email', { shouldValidate: true });
    }
  }, [sendOnDate, delivery, setValue]);

  useEffect(() => {
    trigger('shippingAddressId');
  }, [shippingAddressId]);

  // Read giftId from URL first (needed for urlStepName below)
  const giftIdFromUrl = useMemo(
    () => searchParams.get('giftId'),
    [searchParams],
  );
  // URL is the source of truth for giftId
  const currentGiftId = giftIdFromUrl || giftId;

  // Read step, trial flag from URL
  const urlStepName = useMemo(() => {
    const stepParam = searchParams.get('step');
    if (stepParam) {
      return stepParam;
    }
    // If no step param but tokenAddress and network are present (coming from TokenHome),
    // default to AMOUNT step instead of TOKEN
    const tokenAddress = searchParams.get('tokenAddress');
    const networkParam = searchParams.get('network');
    if (tokenAddress && networkParam && !currentGiftId) {
      return STEP_NAMES.AMOUNT;
    }
    // If no step param, default to TOKEN
    return STEP_NAMES.TOKEN;
  }, [searchParams, currentGiftId]);

  // Fetch gift data if we have a giftId from URL
  const {
    data: giftData,
    isLoading: isLoadingGift,
    isFetching: isFetchingGift,
  } = useGetGiftQuery(currentGiftId, {
    skip: !currentGiftId || !isAuthenticated,
  });

  const fetchedGift = giftData?.gift;

  const [searchTokens] = useLazySearchTokensQuery();
  const lastUpdated = useSelector(selectLastUpdated);

  const baseToken = useSelector((state) =>
    fetchedGift?.baseToken && fetchedGift?.network
      ? selectTokenByNetworkAndAddress(state, fetchedGift.network, fetchedGift.baseToken)
      : null,
  );

  // Lock in the initial authentication state to keep steps array stable
  const initialAuthStateRef = useRef(isAuthenticated);
  if (initialAuthStateRef.current === undefined) {
    initialAuthStateRef.current = isAuthenticated;
  }
  const token = useSelector(selectPrivyToken);

  const { data: gifts } = useGetGiftsQuery({
    skip: !token,
  });

  const showFreeGiftFlag = useFlag('showFreeGift');
  const isUserEligible =
    showFreeGiftFlag && (gifts?.sentGifts?.length === 0 || !gifts?.sentGifts);

  const [isConfiguredGiftEligible, setIsConfiguredGiftEligible] =
    useState(false);
  const [isBonusElegible, setIsBonusElegible] = useState(false);
  const [bonusReason, setBonusReason] = useState(null);
  const [reason, setReason] = useState(null);
  const freeGiftAllInputs = useFlag('freeGiftFullInputs');

  const showTrialExperience = isTrialFlow && isUserEligible;

  const steps = useMemo(
    () =>
      buildSteps(
        delivery,
        initialAuthStateRef.current,
        showTrialExperience,
        freeGiftAllInputs,
        usdAmount,
      ),
    [delivery, showTrialExperience, freeGiftAllInputs, usdAmount],
  );

  // Convert URL step name to index - URL is the source of truth
  const currentStepIndex = useMemo(() => {
    if (!urlStepName) {
      return 0;
    }
    const index = findStepIndexByName(steps, urlStepName);
    // If step not found, default to first step
    return index >= 0 ? index : 0;
  }, [urlStepName, steps]);

  // Special case: if sending or succeeded, always show confirmation (last step)
  const effectiveStepIndex = useMemo(() => {
    if (sendingStatus === 'sending' || sendingStatus === 'success') {
      return steps.length - 1;
    }
    return currentStepIndex;
  }, [currentStepIndex, sendingStatus, steps.length]);

  // Get step name for the current step
  const currentStepName = getStepNameByIndex(steps, effectiveStepIndex);

  // Track when free gift flow is shown
  useEffect(() => {
    if (showTrialExperience) {
      sendEvent(EVENT_FREE_GIFT_FLOW_SHOWN);
    }
  }, [showTrialExperience]);

  useEffect(() => {
    if (showBonus) {
      if (currentStepName === STEP_NAMES.TOKEN) {
        setIsBonusElegible(true);
        setBonusReason(null);
      } else if (currentStepName === STEP_NAMES.AMOUNT || currentStepName === STEP_NAMES.CUSTOMIZE) {
        if (targetToken?.name?.toLowerCase().includes('prestock')) {
          if (delivery === 'mail') {
            setIsBonusElegible(true);
            setBonusReason(null);
          } else {
            setIsBonusElegible(false);
            setBonusReason('Digital delivery is not eligible for bonus');
          }
        } else {
          setIsBonusElegible(false);
          setBonusReason('Only pre-stock tokens are eligible for bonus');
        }
      } else if (currentStepName === STEP_NAMES.SENDER_INFO || currentStepName === STEP_NAMES.SHIPPING) {
        if (targetToken?.name?.toLowerCase().includes('prestock')) {
          if (delivery === 'mail') {
            setIsBonusElegible(true);
            setBonusReason(null);
          } else {
            setIsBonusElegible(false);
            setBonusReason('Digital delivery is not eligible for bonus');
          }
        } else {
          setIsBonusElegible(false);
          setBonusReason('Only pre-stock tokens are eligible for bonus');
        }
      }
    }
  }, [
    showBonus,
    targetToken,
    currentStepName,
    setIsBonusElegible,
    setBonusReason,
    delivery,
  ]);

  useEffect(() => {
    if (showTrialExperience) {
      if (currentStepName === STEP_NAMES.TOKEN) {
        setIsConfiguredGiftEligible(true);
        setReason(null);
      } else if (currentStepName === STEP_NAMES.CUSTOMIZE) {
        if (delivery === 'link') {
          setIsConfiguredGiftEligible(true);
          setReason(null);
        } else {
          setIsConfiguredGiftEligible(false);
          if (delivery === 'mail') {
            setReason('Physical delivery is not eligible for free gifts');
          } else {
            setReason('Email delivery is not eligible for free gifts');
          }
        }
      } else if (currentStepName === STEP_NAMES.DELIVERY) {
        // For DELIVERY step, check if delivery is still 'link' and amount is 1
        if (delivery === 'link') {
          // If AMOUNT step was skipped, assume usdAmount is 1 (default)
          const effectiveAmount = freeGiftAllInputs ? usdAmount : '1';
          if (effectiveAmount === '1' && isUsingUSDC) {
            setIsConfiguredGiftEligible(true);
            setReason(null);
          } else if (!isUsingUSDC) {
            setIsConfiguredGiftEligible(false);
            setReason('Only USDC is eligible for free gifts');
          } else {
            setIsConfiguredGiftEligible(false);
            setReason('Amount must be 1 to receive a free gift');
          }
        } else {
          setIsConfiguredGiftEligible(false);
          if (delivery === 'mail') {
            setReason('Physical delivery is not eligible for free gifts');
          } else {
            setReason('Email delivery is not eligible for free gifts');
          }
        }
      } else if (currentStepName === STEP_NAMES.PREVIEW) {
        // For PREVIEW step, same logic as DELIVERY
        if (delivery === 'link') {
          const effectiveAmount = freeGiftAllInputs ? usdAmount : '1';
          if (effectiveAmount === '1' && isUsingUSDC) {
            setIsConfiguredGiftEligible(true);
            setReason(null);
          } else if (!isUsingUSDC) {
            setIsConfiguredGiftEligible(false);
            setReason('Only USDC is eligible for free gifts');
          } else {
            setIsConfiguredGiftEligible(false);
            setReason('Amount must be 1 to receive a free gift');
          }
        } else {
          setIsConfiguredGiftEligible(false);
          if (delivery === 'mail') {
            setReason('Physical delivery is not eligible for free gifts');
          } else {
            setReason('Email delivery is not eligible for free gifts');
          }
        }
      } else if (currentStepName === STEP_NAMES.AMOUNT || currentStepName === STEP_NAMES.SENDER_INFO) {
        if (delivery === 'link') {
          if (!isUsingUSDC) {
            setIsConfiguredGiftEligible(false);
            setReason('Only USDC is eligible for free gifts');
          } else {
            // If AMOUNT step was skipped, assume usdAmount is 1 (default)
            const effectiveAmount = freeGiftAllInputs ? usdAmount : '1';
            if (effectiveAmount !== '1') {
              setIsConfiguredGiftEligible(false);
              setReason('Amount must be 1 to receive a free gift');
            } else {
              setIsConfiguredGiftEligible(true);
              setReason(null);
            }
          }
        } else {
          setIsConfiguredGiftEligible(false);
          if (delivery === 'mail') {
            setReason('Physical delivery is not eligible for free gifts');
          } else {
            setReason('Email delivery is not eligible for free gifts');
          }
        }
      }
    }
  }, [
    showTrialExperience,
    isUsingUSDC,
    usdAmount,
    delivery,
    currentStepName,
    freeGiftAllInputs,
  ]);

  /**
   * Sync form values from loaded gift data
   */
  const syncFormWithGift = useCallback(
    (giftData) => {
      if (!giftData) return;
      const currentFormValues = getValues();

      // Map backend type field to delivery field
      // type: 'PHYSICAL' → delivery: 'mail'
      // type: 'DIGITAL' with receiver.email → delivery: 'email'
      // type: 'DIGITAL' without receiver.email → delivery: 'link'
      let deliveryFromBackend = null;
      if (giftData.type === 'PHYSICAL') {
        deliveryFromBackend = 'mail';
      } else if (giftData.type === 'DIGITAL') {
        deliveryFromBackend = giftData.receiver?.email ? 'email' : 'link';
      } else if (giftData.delivery) {
        // Fallback to delivery field if it exists
        deliveryFromBackend = giftData.delivery;
      }

      // Convert tokenAmount from backend (raw format) to human-readable format
      let targetTokenAmountFromBackend = undefined;
      if (giftData.tokenAmount) {
        const decimals = targetToken?.decimals ?? 9; // Default to 9 if not available
        targetTokenAmountFromBackend = fromTokenAmount(giftData.tokenAmount, decimals);
      }

      // Map gift API data to form fields
      const formData = {
        network: giftData.network,
        targetTokenAddress: giftData.token,
        usdAmount: giftData.purchaseUsdValue ?? undefined,
        targetTokenAmount: targetTokenAmountFromBackend,
        delivery: deliveryFromBackend ?? currentFormValues.delivery ?? 'link',
        email: giftData.email,
        message: giftData.message,
        image: giftData.imageUrl || giftData.image,
        senderName: giftData.senderName,
        recipientName: giftData.recipientName,
        addressLine1: giftData.addressLine1,
        addressLine2: giftData.addressLine2,
        city: giftData.city,
        state: giftData.state,
        postalCode: giftData.postalCode,
        country: giftData.country,
        formattedAddress: giftData.formattedAddress,
        sendOnDate: giftData.sendOnDate ?? giftData.send_on_date,
      };

      // Set each field individually to trigger validation
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          setValue(key, value, { shouldValidate: false, shouldDirty: false });
        }
      });

      // Set amountSpecifiedInUsd based on backend value
      // amountSpecifiedInUsd = true means USD is source (target token is quoted)
      // amountSpecifiedInUsd = false means target token is source (USD is quoted)
      const amountSpecifiedInUsdFromBackend = giftData.amountSpecifiedInUsd ?? true;
      setValue('amountSpecifiedInUsd', amountSpecifiedInUsdFromBackend, {
        shouldValidate: false,
        shouldDirty: false,
      });

      // Update giftId and gift state
      if (giftData.id) {
        setGiftId(giftData.id);
        setGift(giftData);
      }
    },
    [setValue, getValues, delivery, targetTokenAddress, network, targetToken, fromTokenAmount, setGiftId, setGift],
  );

  /**
   * Sync form values from URL params (tokenAddress, network)
   * Only syncs FROM URL TO form when URL params exist (deep link scenario)
   * Never clears form values - only sets them if URL params are present
   */
  const syncFormWithUrlParams = useCallback(() => {
    const tokenAddress = searchParams.get('tokenAddress');
    const networkParam = searchParams.get('network');
    const formValues = getValues();

    // Only sync if URL params exist (deep link scenario)
    // If they don't exist, leave form values as-is (normal flow)
    if (tokenAddress && networkParam) {
      if (formValues?.targetTokenAddress !== tokenAddress) {
        setValue('targetTokenAddress', tokenAddress, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      if (formValues?.network !== networkParam) {
        setValue('network', networkParam, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
    // Explicitly do NOT clear values if URL params don't exist
    // Form values should persist across step transitions
  }, [searchParams, getValues, setValue]);

  const initializeForm = ({ initialValues }) => {
    setSelectedPaymentToken(null);
    setSelectedPaymentMethodType(null);
    setClaimUrl(null);
    setSendingStatus('idle');
    setFeedback(null);
    form.reset({
      ...defaultInitialValues,
      ...initialValues,
      userEmail,
    });
    clearErrors();
  };

  /**
   * Auto-save gift as draft (creates or updates draft gift)
   * This is called automatically before showing confirmation step
   *
   * @returns {Promise<string>} The giftId of the created/updated draft
   */
  const autoSaveGift = useCallback(async () => {
    // Always use USDC for autosave drafts (will be updated on final submission with selected payment token)
    if (!usdcToken?.address || !targetToken?.address) {
      throw new Error('Missing required tokens');
    }

    // Prepare gift data (same structure as createGift)
    const giftData = {
      delivery: delivery,
      token: targetToken.address,
      baseToken: usdcToken.address,
      network: network,
      imageUrl: image,
      message: message,
      sendOnDate: sendOnDate,
      senderName: senderName,
      amountSpecifiedInUsd: amountSpecifiedInUsd, // true when USD is source, false when target token is source
    };

    // Only send email when delivery is 'email' (backend determines link vs email based on email param)
    if (delivery === 'email') {
      giftData.email = email;
    } else {
      giftData.email = null; // Explicitly set to null for link gifts
    }

    // Send either baseTokenAmount (if amountSpecifiedInUsd is true) or tokenAmount (if false)
    if (amountSpecifiedInUsd) {
      // Calculate baseTokenAmount based on USD amount (drafts use USDC)
      const baseTokenAmount = toTokenAmount(usdAmount, usdcToken.decimals);
      if (!baseTokenAmount || baseTokenAmount === 0) {
        throw new Error('Unable to calculate payment amount. Please try again.');
      }
      giftData.baseTokenAmount = baseTokenAmount;
    } else {
      // Calculate tokenAmount from targetTokenAmount
      const tokenAmount = toTokenAmount(targetTokenAmount, targetToken.decimals);
      if (!tokenAmount || tokenAmount === 0) {
        throw new Error('Unable to calculate token amount. Please try again.');
      }
      giftData.tokenAmount = tokenAmount;
    }

    if (delivery === 'mail') {
      giftData.addressLine1 = addressLine1;
      giftData.addressLine2 = addressLine2;
      giftData.city = city;
      giftData.state = state;
      giftData.postalCode = postalCode;
      giftData.country = country;
      giftData.recipientName = recipientName;
    }

    let createdGiftResult;
    let giftResponse;

    if (currentGiftId) {
      // Update existing draft
      giftResponse = await updateGiftMutation({
        giftId: currentGiftId,
        ...giftData,
      }).unwrap();
      createdGiftResult = giftResponse?.gift || { id: currentGiftId };
    } else {
      // Create new draft
      giftResponse = await createGiftMutation(giftData).unwrap();
      createdGiftResult = giftResponse?.gift;
      setGiftId(createdGiftResult.id);
      setGift(createdGiftResult);
    }

    // Update URL with giftId if we just created one
    if (!currentGiftId && createdGiftResult?.id) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('giftId', createdGiftResult.id);
      // Remove params that are no longer needed once giftId exists
      newParams.delete('tokenAddress');
      newParams.delete('network');
      newParams.delete('usdAmount');
      setSearchParams(newParams, { replace: true });
    }

    return createdGiftResult.id;
  }, [
    usdcToken,
    targetToken,
    usdAmount,
    amountSpecifiedInUsd,
    delivery,
    email,
    message,
    image,
    network,
    addressLine1,
    addressLine2,
    city,
    state,
    postalCode,
    senderName,
    country,
    recipientName,
    sendOnDate,
    currentGiftId,
    createGiftMutation,
    updateGiftMutation,
    searchParams,
    setSearchParams,
    setGiftId,
    setGift,
  ]);

  /**
   * Create a gift with the current form data
   * This centralizes the gift creation logic that was previously in Confirmation component
   *
   * @param {Object} paymentTokenOverride - Optional token to override the selected payment token
   * @returns {Promise<Object>} The created gift object
   */
  const createGift = useCallback(
    async (paymentTokenOverride = null) => {
      // Determine the payment token to use
      const paymentToken =
        paymentTokenOverride ||
        (selectedPaymentMethodType === 'applePay'
          ? usdcToken
          : selectedPaymentToken);

      if (!paymentToken?.address || !targetToken?.address) {
        throw new Error('Missing required tokens');
      }

      // Prepare gift data
      const giftData = {
        delivery: delivery,
        token: targetToken.address, // Output token (what receiver gets)
        baseToken: paymentToken.address, // Input token (what creator pays with)
        network: network,
        imageUrl: image,
        message: message,
        sendOnDate: sendOnDate,
        amountSpecifiedInUsd: amountSpecifiedInUsd, // true when USD is source, false when target token is source
      };

      // Send either baseTokenAmount (if amountSpecifiedInUsd is true) or tokenAmount (if false)
      // Backend will calculate the other one based on amountSpecifiedInUsd flag
      if (amountSpecifiedInUsd) {
        // Calculate baseTokenAmount based on USD value
        let baseTokenAmount;

        if (paymentToken.address === usdcToken.address) {
          // If paying with USDC, use usdAmount directly
          baseTokenAmount = toTokenAmount(usdAmount, paymentToken.decimals);
        } else {
          // For other tokens, get a quote for the USD value
          const orderResult = await triggerCreateOrder({
            inputMint: usdcToken.address,
            outputMint: paymentToken.address,
            amount: toTokenAmount(usdAmount, usdcToken.decimals),
          }).unwrap();

          baseTokenAmount = orderResult.outAmount;
        }

        // Validate baseTokenAmount is set
        if (!baseTokenAmount || baseTokenAmount === 0) {
          throw new Error(
            'Unable to calculate payment amount. Please try again.',
          );
        }

        giftData.baseTokenAmount = baseTokenAmount;
      } else {
        // Calculate tokenAmount from targetTokenAmount (backend will calculate baseTokenAmount)
        if (!targetTokenAmount) {
          throw new Error('Token amount is required.');
        }

        const tokenAmount = toTokenAmount(targetTokenAmount, targetToken.decimals);
        if (!tokenAmount || tokenAmount === 0) {
          throw new Error('Unable to calculate token amount. Please try again.');
        }

        giftData.tokenAmount = tokenAmount;
      }

      // Only send email when delivery is 'email' (backend determines link vs email based on email param)
      if (delivery === 'email') {
        giftData.email = email;
      } else {
        giftData.email = null; // Explicitly set to null for link gifts
      }

      if (delivery === 'mail') {
        giftData.addressLine1 = addressLine1;
        giftData.addressLine2 = addressLine2;
        giftData.city = city;
        giftData.state = state;
        giftData.postalCode = postalCode;
        giftData.senderName = senderName;
        giftData.country = country;
        giftData.recipientName = recipientName;
      }

      // Create or update gift (if giftId exists, update; otherwise create)
      let createdGift;
      let giftResponse;
      if (giftId) {
        giftResponse = await updateGiftMutation({
          giftId,
          ...giftData,
        }).unwrap();
        createdGift = giftResponse?.gift || { id: giftId };
      } else {
        giftResponse = await createGiftMutation(giftData).unwrap();
        createdGift = giftResponse?.gift;
        setGiftId(createdGift.id);
      }

      // Update gift state
      setGift(createdGift);

      return createdGift;
    },
    [
      selectedPaymentMethodType,
      selectedPaymentToken,
      usdcToken,
      targetToken,
      amountSpecifiedInUsd,
      targetTokenAmount,
      usdAmount,
      delivery,
      email,
      message,
      image,
      network,
      sendOnDate,
      addressLine1,
      addressLine2,
      city,
      state,
      postalCode,
      senderName,
      country,
      recipientName,
      triggerCreateOrder,
      createGiftMutation,
      updateGiftMutation,
      giftId,
    ],
  );

  const createTrialGift = useCallback(async () => {
    const session = await getVerisoulSession();
    const sessionId = session?.session_id;
    const giftData = {
      token: targetToken.address,
      network: network,
      imageUrl: image,
      message: message,
      session_id: sessionId,
    };

    const giftResponse = await createTrialGiftMutation(giftData).unwrap();
    setGiftId(giftResponse.gift.id);

    setGift(giftResponse.gift);

    return giftResponse;
  }, [targetToken, message, image, network]);

  /**
   * Submit gift - handles the full flow: create → prepare → sign → broadcast
   * This centralizes the transaction flow logic that was previously in Confirmation component
   */
  const submitGift = useCallback(async () => {
    sendEvent(EVENT_GIFT_FLOW_CREATE_GIFT_BUTTON_CLICKED, {
      is_free_gift: showTrialExperience,
    });
    try {
      setSendingStatus('sending');
      setFeedback('Creating gift...');

      // Step 1: Create or update gift (if giftId exists, it's an update; otherwise create)
      let createdGift;

      // Trial / free-gift flow:
      // - Only allowed when we are in a trial flow (URL param) AND
      //   the cohort flag is on AND our local eligibility checks pass.
      if (isTrialFlow) {
        if (showTrialExperience && isConfiguredGiftEligible) {
          try {
            const trialResult = await createTrialGift();
            const trialGift = trialResult?.gift || trialResult;

            setSendingStatus('success');
            setFeedback(null);
            setClaimUrl(trialResult?.claimUrl || trialGift?.claimUrl || null);
            return;
          } catch (err) {
            setSendingStatus('idle');
            setFeedback(
              reason || 'Trial gift is not available for your account.',
            );
            return;
          }
        } else {
          // In a trial flow, but trial experience is not available or not eligible.
          // Do not create any gift here.
          setSendingStatus('idle');
          setFeedback(
            reason || 'Trial gift is not available for your account.',
          );
          return;
        }
      }

      // Normal paid gift path:
      // - Only when this is NOT a trial flow.
      createdGift = await createGift();

      // Step 2: Prepare transactions (unified endpoint)
      setFeedback('Preparing transactions...');
      const prepareResult = await prepareTransactions(createdGift.id).unwrap();

      if (prepareResult.swapTransaction) {
        // Gift with swap - sign both transactions
        const signedSwapTx = await signAndSerializeTransaction(
          prepareResult.swapTransaction,
        );

        const signedSecondTx = await signAndSerializeTransaction(
          prepareResult.htlcTransaction || prepareResult.transferTransaction,
        );

        setFeedback('Broadcasting transactions...');

        // Unified endpoint handles all gift types with swap
        const result = await broadcastAndTrack({
          giftId: createdGift.id,
          serializedTransaction: signedSwapTx, // Swap transaction
          serializedSecondTransaction: signedSecondTx, // HTLC or transfer transaction
          requestId: prepareResult.requestId, // Jupiter Ultra request ID
        }).unwrap();

        setClaimUrl(result.gift?.claimUrl);

        if (result.transactionId) {
          setSendingStatus('success');
          setFeedback(null);
        }
      } else {
        // Gift without swap - single transaction
        const signedTx = await signAndSerializeTransaction(
          prepareResult.serializedTransaction,
        );

        setFeedback('Broadcasting transaction...');

        // Unified endpoint handles all gift types without swap
        const result = await broadcastAndTrack({
          giftId: createdGift.id,
          serializedTransaction: signedTx,
        }).unwrap();

        setClaimUrl(result.gift?.claimUrl);

        if (result.transactionId) {
          setSendingStatus('success');
          setFeedback(null);
        }
      }

      sendEvent(EVENT_GIFT_FLOW_GIFT_CREATED, {
        id: createdGift.id,
        token: createdGift.token,
        base_token: createdGift.baseToken,
        base_token_amount: createdGift.baseTokenAmount,
        created_date: new Date().toISOString(),
        is_free_gift: showTrialExperience,
      });

      refreshTokenBalances();
      hapticsImpactMedium();
    } catch (error) {
      setSendingStatus('error');
      const errorMessage = parseSolanaError(error);
      sendEvent(EVENT_GIFT_FLOW_GIFT_FAILED, {
        error: errorMessage,
        is_free_gift: showTrialExperience,
      });
      setFeedback(`Error: ${errorMessage}`);
      throw error; // Re-throw so component can handle if needed
    }
  }, [
    createGift,
    prepareTransactions,
    broadcastAndTrack,
    signAndSerializeTransaction,
    parseSolanaError,
    refreshTokenBalances,
    giftId,
    isTrialFlow,
    isUserEligible,
    showTrialExperience,
    isConfiguredGiftEligible,
    reason,
    createTrialGift,
  ]);

  // Sync form with URL params when they change
  // Only sync when URL actually has tokenAddress/network params (deep link scenario)
  // Skip syncing if giftId exists - backend is source of truth in that case
  useEffect(() => {
    // If giftId exists, don't sync from URL params (load from backend instead)
    if (currentGiftId) {
      return;
    }

    const tokenAddress = searchParams.get('tokenAddress');
    const networkParam = searchParams.get('network');

    // Only sync if URL params exist (deep link scenario)
    // If they don't exist, we're in normal flow and form values should persist
    if (tokenAddress && networkParam) {
      syncFormWithUrlParams();
    }
  }, [syncFormWithUrlParams, searchParams, currentGiftId]);

  // Fetch tokens if missing when syncing draft gift
  useEffect(() => {
    if (
      fetchedGift?.id &&
      fetchedGift.id === currentGiftId &&
      lastUpdated > 0
    ) {
      const tokensToFetch = [];

      if (!targetToken && fetchedGift.token) {
        tokensToFetch.push(fetchedGift.token);
      }

      if (
        !baseToken &&
        fetchedGift.baseToken &&
        fetchedGift.baseToken !== fetchedGift.token
      ) {
        tokensToFetch.push(fetchedGift.baseToken);
      }

      if (tokensToFetch.length > 0) {
        searchTokens({
          addresses: tokensToFetch,
        });
      }
    }
  }, [
    fetchedGift,
    currentGiftId,
    targetToken,
    baseToken,
    lastUpdated,
    searchTokens,
  ]);

  // Sync form with fetched gift data when it loads (only once per giftId)
  const syncedGiftIdRef = useRef(null);
  useEffect(() => {
    if (
      fetchedGift?.id &&
      fetchedGift.id === currentGiftId &&
      syncedGiftIdRef.current !== currentGiftId
    ) {
      if (!targetTokenAddress || !network) {
        setValue('targetTokenAddress', fetchedGift.token);
        setValue('network', fetchedGift.network);
      } else if (targetTokenAddress && network) {
        syncFormWithGift(fetchedGift);
        syncedGiftIdRef.current = currentGiftId;
      }
    }
    if (currentGiftId && currentGiftId !== syncedGiftIdRef.current) {
      syncedGiftIdRef.current = null;
    }
  }, [
    fetchedGift,
    currentGiftId,
    syncFormWithGift,
    targetTokenAddress,
    network,
    setValue,
  ]);

  // Autosave logic:
  // - If giftId exists: always autosave on step change
  // - If no giftId: autosave if authenticated and have required fields (imageUrl, token, amount, network)
  // - Skip confirmation step (final submission handles that)
  // Only runs on step change, auth change, or giftId change
  const lastAutoSaveAttemptRef = useRef(0);

  useEffect(() => {
    const handleAutosave = async () => {
      // Skip confirmation step (final submission handles that)
      if (currentStepName === STEP_NAMES.CONFIRMATION) {
        return;
      }

      // If giftId exists, always autosave (updating existing draft)
      // Otherwise, check if we have required fields for first save
      const formValues = getValues();
      const currentAmountSpecifiedInUsd = formValues?.amountSpecifiedInUsd ?? true;

      // Check for amount - must be a non-empty string/number
      const hasAmount = currentAmountSpecifiedInUsd
        ? formValues?.usdAmount
        : formValues?.targetTokenAmount;

      const hasRequiredFields = Boolean(
        isAuthenticated &&
        formValues?.image &&
        formValues?.targetTokenAddress &&
        formValues?.network &&
        hasAmount
      );

      if (!currentGiftId && !hasRequiredFields) {
        return;
      }

      // Rate limit: don't retry within 2 seconds of last attempt
      const now = Date.now();
      if (now - lastAutoSaveAttemptRef.current < 2000) {
        return;
      }

      // Prevent concurrent autosaves
      if (isAutoSaving) {
        return;
      }

      lastAutoSaveAttemptRef.current = now;
      setIsAutoSaving(true);
      try {
        await autoSaveGift();
      } catch (error) {
        // Silently fail - don't block user
        console.error('Autosave failed:', error);
      } finally {
        setIsAutoSaving(false);
      }
    };

    handleAutosave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStepName,
    currentGiftId,
    isAuthenticated,
    isAutoSaving,
  ]);

  // Calculate step component and loading state
  const currentStepConfig = steps[effectiveStepIndex];
  let Component = currentStepConfig?.component || STEP_COMPONENTS.TokenSelector;
  const isConfirmationStep =
    currentStepConfig?.name === STEP_NAMES.CONFIRMATION;

  if (isConfirmationStep) {
    // Show a skeleton that matches the Confirmation screen while loading necessary data
    const isConfirmationLoading =
      sendingStatus === 'idle' &&
      (isAutoSaving ||
        (currentGiftId && !fetchedGift && (isLoadingGift || isFetchingGift)));

    if (isConfirmationLoading) {
      Component = ConfirmationSkeleton;
    }
  }

  const currentTitle = currentStepConfig?.title || '';
  const buttonLabel = currentStepConfig?.buttonLabel || 'Continue';

  const shouldShowBackButton =
    effectiveStepIndex > 0 &&
    (sendingStatus === 'idle' || effectiveStepIndex < steps.length - 1);

  // Get previous step name for back navigation
  let previousStepName = null;
  if (effectiveStepIndex > 0) {
    let prevIdx = effectiveStepIndex - 1;
    // When authenticated, skip SenderInfo when navigating backwards
    while (prevIdx >= 0) {
      const stepName = steps[prevIdx]?.name;
      if (stepName === STEP_NAMES.SENDER_INFO && isAuthenticated) {
        prevIdx -= 1;
        continue;
      }
      previousStepName = getStepNameByIndex(steps, prevIdx);
      break;
    }
  }

  // Get next step name for continue navigation
  let nextStepName = null;
  if (effectiveStepIndex < steps.length - 1) {
    let nextIdx = effectiveStepIndex + 1;
    // When authenticated, skip SenderInfo when navigating forwards
    while (nextIdx < steps.length) {
      const stepName = steps[nextIdx]?.name;
      if (stepName === STEP_NAMES.SENDER_INFO && isAuthenticated) {
        nextIdx += 1;
        continue;
      }
      nextStepName = getStepNameByIndex(steps, nextIdx);
      break;
    }
  }

  // Helper to update step in URL (for navigation)
  const updateStepInUrl = useCallback(
    (stepName) => {
      const newParams = new URLSearchParams(searchParams);
      const formValues = getValues();

      // Update step
      if (stepName === STEP_NAMES.TOKEN) {
        newParams.delete('step');
        // When navigating to TOKEN step, remove tokenAddress and network params
        // so user can change the token without being redirected back to AMOUNT
        newParams.delete('tokenAddress');
        newParams.delete('network');
      } else {
        newParams.set('step', stepName);
      }

      // If giftId exists, we only need giftId in URL (backend is source of truth)
      // Remove tokenAddress, network, usdAmount as they're redundant
      if (currentGiftId) {
        newParams.set('giftId', currentGiftId);
        newParams.delete('tokenAddress');
        newParams.delete('network');
        newParams.delete('usdAmount');
      } else {
        // Before giftId exists, sync core selection params to URL for deep linking
        // These are the minimal params needed to restore the gift flow
        // Only add them if we're not on TOKEN step (where user might want to change token)
        if (stepName !== STEP_NAMES.TOKEN) {
          if (formValues.targetTokenAddress) {
            newParams.set('tokenAddress', formValues.targetTokenAddress);
          }
          if (formValues.network) {
            newParams.set('network', formValues.network);
          }
          if (formValues.usdAmount) {
            newParams.set('usdAmount', formValues.usdAmount);
          }
        }
      }

      setSearchParams(newParams, { replace: false });
    },
    [searchParams, setSearchParams, currentGiftId, getValues],
  );

  // If the user has become authenticated, they should not see SenderInfo anymore.
  // When on SenderInfo and auth flips to true (e.g., progressive onboarding),
  // immediately advance to the next step so navigating back/forward never shows it.
  useEffect(() => {
    if (
      isAuthenticated &&
      currentStepName === STEP_NAMES.SENDER_INFO &&
      nextStepName
    ) {
      updateStepInUrl(nextStepName);
    }
  }, [isAuthenticated, currentStepName, nextStepName, updateStepInUrl]);

  // Guard: If user reaches Confirmation step without token/network, redirect to Token step.
  // This handles deep-links or edge cases where form state is missing.
  useEffect(() => {
    if (currentStepName === STEP_NAMES.CONFIRMATION) {
      // If we have a giftId, we'll load the gift data (which has token/network)
      // so no redirect needed in that case.
      if (currentGiftId) {
        return;
      }

      // If we don't have token/network in form, redirect to Token step
      if (!targetTokenAddress || !network) {
        updateStepInUrl(STEP_NAMES.TOKEN);
      }
    }
  }, [
    currentStepName,
    currentGiftId,
    targetTokenAddress,
    network,
    updateStepInUrl,
  ]);

  return (
    <GiftFormContext.Provider
      value={{
        handleSubmit,
        control,
        errors,
        isValid,
        isSubmitting,
        setValue,
        getValues,
        claimUrl,
        gift,
        setGift,
        setFeedback,
        setClaimUrl,
        setSendingStatus,
        clearErrors,
        trigger,
        payingWith,
        setPayingWith,
        isUsingUSDC,
        delivery,
        email,
        message,
        image,
        network,
        sendingStatus,
        feedback,
        usdAmount,
        selectedPaymentToken,
        setSelectedPaymentToken,
        selectedPaymentMethodType,
        setSelectedPaymentMethodType,
        targetTokenAddress,
        targetTokenAmount,
        initializeForm,
        setIsUsingUSDC: (newValue) => setValue('isUsingUSDC', newValue),
        amountSpecifiedInUsd,
        senderName,
        setAmountSpecifiedInUsd: (newValue) => setValue('amountSpecifiedInUsd', newValue),
        addressLine1,
        addressLine2,
        city,
        recipientName,
        state,
        postalCode,
        country,
        formattedAddress,
        sendOnDate,
        // Gift creation functions
        createGift,
        submitGift,
        // Gift ID state
        giftId,
        setGiftId,
        // Expose tokens for components that need them
        usdcToken,
        targetToken,
        // Navigation and step state
        currentStep: effectiveStepIndex,
        currentStepName,
        totalSteps: steps.length,
        currentTitle,
        Component,
        onBackStepName: previousStepName,
        onNextStepName: nextStepName,
        shouldShowBackButton,
        buttonLabel,
        updateStepInUrl,
        // Gift data from API
        fetchedGift,
        isLoadingGift:
          isLoadingGift || isFetchingGift || (!giftData && currentGiftId),
        isAutoSaving,
        // Sync functions
        syncFormWithGift,
        syncFormWithUrlParams,
        isConfiguredGiftEligible,
        reason,
        showFreeGift: showTrialExperience,
        isUserEligible,
        showBonus,
        isBonusElegible,
        bonusReason,
        lpd,
      }}
    >
      {children}
    </GiftFormContext.Provider>
  );
};

export const useGiftForm = () => {
  const ctx = useContext(GiftFormContext);
  if (!ctx)
    throw new Error('useGiftForm must be used within a GiftFormProvider');
  return ctx;
};

/**
 * Handle Enter key to move to next field
 * @param {KeyboardEvent} e - The keyboard event
 * @param {React.RefObject} nextRef - Ref to the next input field (IonInput)
 */
export const handleKeyboardNext = async (e, nextRef) => {
  if (e.key === 'Enter' && nextRef?.current) {
    e.preventDefault();
    e.stopPropagation();
    await nextRef.current.setFocus();
  }
};

/**
 * Handle Enter key on the last field
 * If form is valid and onProceed is provided, proceed to next step
 * Otherwise, just close the keyboard
 * @param {KeyboardEvent} e - The keyboard event
 * @param {React.RefObject} lastFieldRef - Ref to the last input field (IonInput)
 * @param {Object} options - Options object
 * @param {Function} options.onProceed - Function to call when form is valid
 * @param {boolean} options.isValidForm - Whether the form is valid
 */
export const handleKeyboardDone = async (
  e,
  lastFieldRef,
  { onProceed, isValidForm },
) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    e.stopPropagation();

    if (isValidForm && onProceed) {
      onProceed();
    } else if (lastFieldRef?.current) {
      const input = await lastFieldRef.current.getInputElement();
      input?.blur();
    }
  }
};

export { giftFormSchema, GiftFormContext };
